<template>
  <div class="relative z-0 rounded-lg border border-gray-200">
    <div
      class="flex cursor-pointer items-center justify-between bg-white p-4 shadow-sm transition-colors"
      data-cy="funder-placements-table-header"
      @click="toggleCollapsed"
    >
      <div class="flex items-center gap-4 overflow-hidden min-w-0 flex-1">
        <img
          v-if="funderLogo"
          :src="funderLogo"
          :alt="funderName"
          class="h-8 w-8 rounded object-contain shrink-0"
        />

        <div class="flex items-center space-x-4 shrink-0">
          <span class="text-sm font-bold text-headline whitespace-nowrap">
            {{ funderName }}
          </span>
        </div>

        <div
          v-show="isCollapsed"
          class="relative flex-1 min-w-0 overflow-hidden mr-4"
        >
          <div class="flex items-center space-x-2">
            <funder-placement-product-tag
              v-for="placement in filteredFunderPlacements"
              :key="placement.id"
              :placement
            />
          </div>
          <div
            class="pointer-events-none absolute right-0 top-0 h-full w-12 bg-linear-to-l from-white to-transparent"
          />
        </div>
      </div>
      <icon-chevron-right
        class="transform cursor-pointer"
        :class="isCollapsed ? 'rotate-90' : '-rotate-90'"
      />
    </div>
    <div v-show="!isCollapsed" class="transition-all duration-200">
      <lf-table
        v-model:pin-enabled="pinEnabled"
        v-model:pin-last-enabled="pinLastEnabled"
        class="freeze-last freeze-border-last"
        :columns="placementColumns"
        :data="filteredFunderPlacements"
        :lazy-load="false"
        custom-id="funder-placements-table"
        data-cy="lf-table"
        pin-option
        row-is-clickable
        show-last-column-pin
        :offers="activePlacementOfferDetails"
        @row-clicked="openViewOffersModal"
      />
      <offer-modal
        v-if="showOfferDetailsModal"
        :offer-details="activePlacementOfferDetails"
        view-only
        :loading-data="isOfferLoading"
        :active-funder-name="funderName"
        data-cy="offer-modal"
        @close="showOfferDetailsModal = false"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";

import { PLACEMENT_STATUS_DECLINED } from "@/helpers/constants";
import { formatDateCustom } from "@/helpers/formatting";
import { isValidForSubmittedPlacements } from "@/helpers/placements";
import { useAuth } from "@/hooks/auth";
import { usePromiseWrapper } from "@/hooks/common";
import applicationsApiService from "@/services/modules/applications";

import FunderFiles from "@/components/deals/placement/FunderFiles.vue";
import FunderPlacementProductTag from "@/components/deals/placement/FunderPlacementProductTag.vue";
import OfferDetailsButton from "@/components/deals/placement/OfferDetailsTag.vue";
import PlacementProductCell from "@/components/deals/placement/PlacementProductCell.vue";
import PlacementUrlInput from "@/components/deals/placement/PlacementUrlInput.vue";
import IconChevronRight from "@/components/icons/IconChevronRight.vue";
import PlacementAppropriateStatus from "@/components/ui/PlacementAppropriateStatus.vue";
import PlacementDeclineDescription from "@/components/ui/PlacementDeclineDescription.vue";
import PlacementDeclineReason from "@/components/ui/PlacementDeclineReason.vue";
import PlacementDeclineSubReason from "@/components/ui/PlacementDeclineSubReason.vue";
import LfTable from "@/components/ui/table/LfTable.vue";
import WorkFlowStatusCell from "@/components/ui/table/cell/WorkFlowStatusCell.vue";
import OfferModal from "@/views/deals/components/OfferModal.vue";

import type { IPlacementTableRow, OfferDetails } from "@/models/offers";
import type { PlacementFilters } from "@/models/placement";

type Props = {
  funderName: string;
  funderLogo?: string;
  funderPlacements: IPlacementTableRow[];
  filters?: PlacementFilters;
};

const { funderName, funderLogo, funderPlacements, filters } =
  defineProps<Props>();

const { t } = useI18n();
const { isClient } = useAuth();

const filteredFunderPlacements = computed(() => {
  return funderPlacements.filter((pl) =>
    isValidForSubmittedPlacements(pl, filters)
  );
});

const isCollapsed = ref(true);
const pinEnabled = ref(true);
const pinLastEnabled = ref(true);
const showOfferDetailsModal = ref(false);
const activePlacementOfferDetails = ref<OfferDetails[]>([]);

const { fetchWrapper: getPlacementOffers, loading: isOfferLoading } =
  usePromiseWrapper(async (placementId: string) => {
    activePlacementOfferDetails.value =
      await applicationsApiService.getAllOffersForPlacement(placementId);
  });

const toggleCollapsed = () => {
  isCollapsed.value = !isCollapsed.value;
};

const somePlacementsDeclined = computed(() =>
  funderPlacements.some(
    (placement) => placement.status === PLACEMENT_STATUS_DECLINED
  )
);

const canSendFiles = computed(() =>
  funderPlacements.every((placement) => placement.can_send_files)
);

const placementColumns = computed(() => {
  const columnsForDeclinedPlacements = somePlacementsDeclined.value
    ? [
        {
          label: t("DEALS.PLACEMENT.TABLE_COLUMNS.DECLINE_REASON"),
          key: "decline_reason_id",
          component: PlacementDeclineReason,
        },
        {
          label: t("DEALS.PLACEMENT.TABLE_COLUMNS.DECLINE_SUB_REASON"),
          key: "decline_subreason_id",
          component: PlacementDeclineSubReason,
        },
        {
          label: t("DEALS.PLACEMENT.TABLE_COLUMNS.DECLINE_DESCRIPTION"),
          key: "decline_reason",
          component: PlacementDeclineDescription,
        },
      ]
    : [];

  return [
    {
      label: t("DEALS.PLACEMENT.TABLE_COLUMNS.PRODUCT"),
      key: "offer_template",
      component: PlacementProductCell,
    },
    {
      label: t("DEALS.PLACEMENT.TABLE_COLUMNS.PLACEMENT_STATUS"),
      key: "status",
      component: PlacementAppropriateStatus,
    },
    !isClient
      ? {
          label: t("DEALS.PLACEMENT.TABLE_COLUMNS.SUBMISSION"),
          key: "submission",
        }
      : null,
    {
      label: t("COMMON.WORKFLOW_BUILDER_TEMPLATE"),
      key: "workflowTemplateName",
    },
    {
      label: t("DEALS.PLACEMENT.TABLE_COLUMNS.FUNDER_PRODUCT"),
      key: "client_offer_template",
    },
    {
      label: t("DEALS.PLACEMENT.TABLE_COLUMNS.RISK_TIER"),
      key: "risk_tiers",
      component: "risk-tier-list",
    },
    {
      label: t("DEALS.PLACEMENT.TABLE_COLUMNS.SUBMISSION_TIME"),
      key: "created_at",
      class: "whitespace-nowrap",
      formatter: (value: string) =>
        formatDateCustom(value, "MMMM d, yyyy h:mm a", true),
    },
    {
      label: t("DEALS.PLACEMENT.TABLE_COLUMNS.LAST_RESUBMITTED"),
      key: "resubmitted_at",
      class: "whitespace-nowrap",
      formatter: (value: string) =>
        value ? formatDateCustom(value, "MMMM d, yyyy h:mm a", true) : "-",
    },
    {
      label: t("COMMON.UPDATED_AT"),
      key: "updated_at",
      class: "whitespace-nowrap",
      formatter: (value: string) =>
        formatDateCustom(value, "MMMM d, yyyy h:mm a", true),
    },
    {
      label: `${t("COMMON.WORKFLOW")} ${t("COMMON.STATUS")}`,
      key: "workflow_status.status",
      component: WorkFlowStatusCell,
      props: { "data-cy": "workflow-status-pill" },
    },
    {
      label: `${t("COMMON.WORKFLOW")} ${t("COMMON.SUB_STATUS")}`,
      key: "workflow_status.sub_status",
      component: WorkFlowStatusCell,
      props: { "data-cy": "workflow-sub-status-pill" },
    },
    {
      label: t("DEALS.PLACEMENT.TABLE_COLUMNS.SUBMITTED_BY"),
      key: "submittedBy",
    },
    canSendFiles.value
      ? {
          label: `${t("COMMON.APPLICATION")} ${t("COMMON.DOCUMENTS")}`,
          key: "files_sent",
          component: FunderFiles,
        }
      : null,
    ...columnsForDeclinedPlacements,
    {
      label: t("COMMON.URL"),
      key: "url",
      component: PlacementUrlInput,
    },
    {
      label: t("DEALS.DEAL_PROGRESS.OFFERS"),
      key: "action",
      component: OfferDetailsButton,
      headerClass: "whitespace-nowrap min-w-[200px]",
      columnClass: "whitespace-nowrap min-w-[200px]",
    },
  ].filter((col) => col !== null);
});

const openViewOffersModal = async (row: string) => {
  const placement = funderPlacements.find(
    (placement) => placement.placement_id === row
  );
  if (!placement?.offers_count) {
    return;
  }
  await getPlacementOffers(row);
  showOfferDetailsModal.value = true;
};

defineExpose({
  funderName,
  collapseTable() {
    isCollapsed.value = true;
  },
});
</script>
